import { Box, CircularProgress, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Style } from "./Style";
import { CustomButton } from "..";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { selectUser } from "../../store/slice/user";
import { useSelector } from "react-redux";
import { getUpcomingTickets, getOldTickets } from "../../services/ticketHistory";

// The just-purchased ticket can land in either bucket (test/sandbox events
// are frequently already in the past, landing it in "old" instead of
// "upcoming"), and both endpoints return oldest-first, so the latest
// purchase is whichever record has the highest id, not the first one back.
const fetchLatestTicket = async (token) => {
  const [upcoming, old] = await Promise.all([
    getUpcomingTickets({ token, page: 1, number: 50 }).catch(() => null),
    getOldTickets({ token, page: 1, number: 50 }).catch(() => null),
  ]);
  const items = [
    ...(upcoming?.data?.data?.data || []),
    ...(old?.data?.data?.data || []),
  ];
  if (!items.length) return null;
  return items.reduce((latest, item) => (item.id > latest.id ? item : latest));
};

const TicketStatus = ({ success }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, token } = useSelector(selectUser);
  const isArabic = language === "ar";
  const [ticket, setTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(!!success);

  useEffect(() => {
    if (!success || !token) return;
    let cancelled = false;

    const load = async (retry) => {
      const latest = await fetchLatestTicket(token).catch(() => null);
      if (cancelled) return;
      if (!latest && !retry) {
        // Webhook-created tickets can lag the redirect by a couple of
        // seconds — give it one more try before giving up.
        setTimeout(() => load(true), 2500);
        return;
      }
      setTicket(latest);
      setTicketLoading(false);
    };
    load(false);

    return () => {
      cancelled = true;
    };
  }, [success, token]);

  const event = ticket?.ticket?.event;
  const eventDate =
    event?.event_date && dayjs(event.event_date).isValid()
      ? dayjs(event.event_date).format("ddd, MMM D")
      : "";
  const eventTime =
    event?.event_time && dayjs(event.event_time, "HH:mm:ss").isValid()
      ? dayjs(event.event_time, "HH:mm:ss").format("h:mm A")
      : event?.event_time;

  return (
    <Box sx={Style.main}>
      <Box sx={success ? Style.badgeCircle : Style.badgeCircleFail}>
        {success ? <CheckIcon fontSize="inherit" /> : <CloseIcon fontSize="inherit" />}
      </Box>
      <Typography sx={Style.statusText(isArabic)}>
        {success
          ? t("ticket_status_page.booking_confirmed")
          : t("ticket_status_page.failure")}
      </Typography>
      <Typography sx={Style.message(isArabic)}>
        {success
          ? t("ticket_status_page.digital_tickets_sent")
          : t("ticket_status_page.payment_negative_status")}
      </Typography>

      {success && ticketLoading && (
        <CircularProgress size={28} color="secondary" sx={{ my: 1 }} />
      )}

      {success && !ticketLoading && ticket && event && (
        <>
          <Box sx={Style.ticketCard}>
            <Box sx={Style.ticketCardBody(isArabic)}>
              <Typography sx={Style.admitOneLabel(isArabic)}>
                {t("ticket_status_page.admit_one")}
              </Typography>
              <Typography sx={Style.ticketEventName(isArabic)}>
                {event?.translation?.[language]?.name}
              </Typography>
              <Typography sx={Style.ticketMetaLine(isArabic)}>
                {[eventDate, eventTime].filter(Boolean).join(" · ")}
              </Typography>
              <Typography sx={Style.ticketLocation(isArabic)}>
                {event?.translation?.[language]?.address}
              </Typography>
              <Typography sx={Style.ticketQty(isArabic)}>
                {ticket?.ticket?.is_free
                  ? t("event_detail_page.free")
                  : ticket?.ticket?.translation?.[language]?.name ||
                    t("ticket_status_page.ticket_label")}
                {ticket?.qty ? ` × ${ticket.qty}` : ""}
              </Typography>
            </Box>
            <Box sx={Style.ticketStub}>
              <Box sx={Style.ticketNotch("top")} />
              {ticket?.qr_code?.full_qr_code ? (
                <Box
                  component="img"
                  src={ticket.qr_code.full_qr_code}
                  alt="QR code"
                  sx={Style.ticketQrImg}
                />
              ) : null}
              <Box sx={Style.ticketNotch("bottom")} />
            </Box>
          </Box>
          <Typography sx={Style.orderNumber(isArabic)}>
            {t("ticket_status_page.order_number", { id: ticket?.id })}
          </Typography>
        </>
      )}

      <CustomButton
        onClick={() => navigate(success ? "/my-tickets" : -2)}
        buttonText={
          success ? t("ticket_status_page.done") : t("ticket_status_page.try_again")
        }
        color="secondary"
        sx={Style.actionButton(isArabic)}
      />
      <CustomButton
        onClick={() => navigate("/")}
        buttonText={t("ticket_status_page.home")}
        color="black"
        sx={Style.backButton(isArabic)}
      />
    </Box>
  );
};

export default TicketStatus;
